import { useCallback, useRef, useState } from "react";
import { signOut as endSession } from "../utils/auth";

/**
 * Sign the shopper out from any surface, once.
 *
 * `signOut` in utils/auth does the work — ends the backend session, drops the
 * agent socket, clears storage and cookies — and always finishes by sending
 * the browser to "/": a full load, which resets every context, the avatar and
 * the socket far more reliably than unwinding them in place. Nothing runs
 * after that navigation, so `signingOut` is deliberately never reset on
 * success: the control showing it leaves with the page, and resetting it
 * first only made the label flick back to "Sign out" for a frame.
 *
 * What the hook adds is the pending state for the control that was pressed
 * and a guard against a second press while the backend call is in flight.
 * Used by the top bar's account menu (UserMenu) and the account page.
 */
export function useSignOut() {
  const [signingOut, setSigningOut] = useState(false);
  const inFlight = useRef(false);

  const signOut = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSigningOut(true);
    try {
      // No alert: landing on the signed-out home page is the confirmation.
      await endSession(false);
    } catch (error) {
      // endSession handles its own failures and still redirects, so this is
      // only for something thrown before it got that far. Let the shopper try
      // again rather than leave a control stuck on "Signing out…".
      console.error("[useSignOut] sign-out failed:", error);
      inFlight.current = false;
      setSigningOut(false);
    }
  }, []);

  return { signingOut, signOut };
}
